import PostRequest from "csi/comm/PostRequest";
import { LabelCalculationStore } from "../LabelCalculationStore";
import { C8Node } from "csi/typings";
import { ILocaleProvider } from "designSystem/locale";
import { action, makeObservable, observable } from "mobx";
import { createContext, useContext } from "react";

const ALLERGENS: string = "Allergens";
const INGREDIENTS: string = "Ingredients";
const NUTRIENTS: string = "Nutrients";
const ITEM_TYPES: string[] = [ALLERGENS, INGREDIENTS, NUTRIENTS];
const UPDATE_OP: string = "Update";
const RESET_OP: string = "Reset";
const NOCALC_OP: string = "NoCalculation";
const OPERATIONS: string[] = [UPDATE_OP, RESET_OP, NOCALC_OP];
const CALCULATE_ACTIONS: string = "FoodLabelDataSheetRevision.Action.Calculate";
const NO_CHECK = 0;
const FIRST_CHECKBOX = 1;
const SECOND_CHECKBOX = 2;

export default class RecipeLabelStore extends LabelCalculationStore {
	@observable private _globalIngredientChecked: number;
	/** Indicates the FormulatedProduct operation that we are executing for this form */
	readonly formulatedProductOperation: string;
	readonly globalIngredientUpdate: string;

	@action setGlobalIngredientCheck(newValue: number): void {
		this._globalIngredientChecked = newValue;
	}

	/** 
	 * Return a number indicating which checkbox is checked 
	 * 0 for none, 1 for 1st checkbox, 2 for 2nd checkbox
	 */
	get globalIngredientChecked() : number {
		return this._globalIngredientChecked;
	}

	/** 
	 * @override
	 * Use updateIngredientOptions and resetIngredientOptions to include Global Ingredient List checkbox.
	 */
	get ingredientOptions(): object[] {
		const { locale, dataSheetSections } = this;
		
		return [
			this.updateIngredientOption,
			this.resetIngredientOption,
			{
				name: locale.resource(NOCALC_OP),
				invalid: false,
				value: NOCALC_OP,
				disabled: !dataSheetSections.includes("LabelDataSheetSections:FoodIngredient"),
			},
		];
	}

	/** Update the store with the user's selection for what to update */
	@action updateRecipeLabelAction(targetItemType: string, newValue: string, globalValue?: boolean, globalCheckbox?: number): boolean {
		if (targetItemType === INGREDIENTS) {
			if (globalValue != undefined) {
				this._globalIngredientChecked = globalCheckbox;
			}
		}
		if (OPERATIONS.includes(newValue) && ITEM_TYPES.includes(targetItemType)) {
			switch (targetItemType) {
				case ALLERGENS:
					this.setAllergenChecked(newValue);
					break;
				case INGREDIENTS:
					this.setIngredientChecked(newValue);
					break;
				case NUTRIENTS:
					this.setNutrientChecked(newValue);
					break;
			}
		}
		return true;
	}

	/** Return the Update Option for Ingredient for the form */
	get updateIngredientOption(): object {
		const ingredOperation = CALCULATE_ACTIONS + INGREDIENTS;
		const { locale, globalIngredientUpdate, ingredientChecked, globalIngredientChecked, dataSheetSections } = this;
		return {
			name: locale.resource(`${ingredOperation}_Update`),
			invalid: false,
			value: UPDATE_OP,
			helperText:locale.resource(`${ingredOperation}_DescriptionForUpdate`),
			checkboxProps: {
				labelProps:{ label: globalIngredientUpdate },
				name: globalIngredientUpdate + UPDATE_OP, 
				onChange:(event, val): void => {
					if (val) {
						this.setGlobalIngredientCheck(FIRST_CHECKBOX);
					} else {
						this.setGlobalIngredientCheck(NO_CHECK);
					}
					this.updateRecipeLabelAction("Global", (val).toString());
				},
				checked: (globalIngredientChecked === FIRST_CHECKBOX),
				disabled: (ingredientChecked !== UPDATE_OP),
			},
			disabled: !dataSheetSections.includes("LabelDataSheetSections:FoodIngredient"),
		};
	}

	/** Return the Reset Option for Ingredient for the form */
	get resetIngredientOption(): object {
		const ingredOperation = CALCULATE_ACTIONS + INGREDIENTS;
		const { locale, globalIngredientUpdate, ingredientChecked, globalIngredientChecked, dataSheetSections } = this;
		return {
			name: this.locale.resource(`${ingredOperation}_Reset`),
			invalid: false,
			value: RESET_OP,
			helperText:locale.resource(`${ingredOperation}_DescriptionForReset`),
			checkboxProps: {
				labelProps:{ label:globalIngredientUpdate },
				name:globalIngredientUpdate + RESET_OP, 
				onChange:(event, val): void => {
					if (val) {
						this.setGlobalIngredientCheck(SECOND_CHECKBOX);
					} else {
						this.setGlobalIngredientCheck(NO_CHECK);
					}
					this.updateRecipeLabelAction("Global", (val).toString());
				},
				checked: (globalIngredientChecked === SECOND_CHECKBOX),
				disabled: (ingredientChecked !== RESET_OP),
			},
			disabled: !dataSheetSections.includes("LabelDataSheetSections:FoodIngredient"),
		};
	}

	/** Return the post request which contains the user's preferences and the specific operation we are doing */
	buildRequest = (): PostRequest => {
		const newRequest = new PostRequest("FormulatedProduct", this.formulatedProductOperation);
		newRequest.addParameter("URL", this.documentCnl);
		
		if (this.allergenChecked !== NOCALC_OP) {
			newRequest.addParameter("CalculateAllergens", this.allergenChecked);
		}
		if (this.ingredientChecked !== NOCALC_OP) {
			newRequest.addParameter("CalculateIngredients", this.ingredientChecked);
		}
		if (this.nutrientChecked !== NOCALC_OP) {
			newRequest.addParameter("CalculateNutrients", this.nutrientChecked);;
		}
		
		newRequest.addParameter("UpdateGlobalIngredientList", this.globalIngredientChecked != 0);

		return newRequest;
	};

	constructor(document: C8Node, loc: ILocaleProvider, dataSheetSections: string[], formulatedProdOp: string) {
		super(document, loc, dataSheetSections);
		this.formulatedProductOperation = formulatedProdOp;
		this.globalIngredientUpdate =  this.locale.resource("UpdateGlobalIngredientList");
		makeObservable(this);
	}
}

export const RecipeLabelStoreContext = createContext<RecipeLabelStore>(undefined);
export const useRecipeLabelStore = (): RecipeLabelStore => useContext(RecipeLabelStoreContext);